/**
 * Runtime context store.
 *
 * A module-level `Record<componentId, RuntimeContextCapture>` that
 * captures the most recent rule-context bag passed to each registered
 * component. The admin config page reads from it to surface the actual
 * live values of context variables alongside the rule-authoring form so
 * authors can eyeball what `task_type`, `claim_amount`, … currently
 * resolve to rather than guess.
 *
 * Characteristics:
 *
 * - **Per-class, last-write-wins.** Multiple instances of the same
 *   component share a single slot — the last render wins. We only need
 *   one "what is this variable for the current claim?" snapshot, not a
 *   per-instance history.
 *
 * - **Persisted to localStorage.** Unlike `runtimePropsStore` (which is
 *   in-memory because props can contain callbacks / React elements), the
 *   context bag is JSON-serialisable by construction (page-supplied
 *   primitives), so we persist it. This means the admin can bookmark or
 *   open the config page in a fresh tab and still see the last captured
 *   values.
 *
 * - **Degrades gracefully.** Mirrors `configModeStore`'s try/catch
 *   pattern — any localStorage failure (private mode, quota, disabled)
 *   is swallowed; the in-memory map still works for the current session.
 *
 * - **No React imports.** Writes happen from the `withComponentRules`
 *   HOC; reads happen from the admin config page render.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export const RULE_CONTEXT_STORAGE_KEY = 'kusudama_rule_context_v1';

export interface RuntimeContextCapture {
  /** Variable bag exactly as it was last passed to the HOC, normalised
   *  via {@link serialiseContext} so it round-trips through JSON. */
  context: Record<string, unknown>;
  /** ISO 8601 timestamp of the capture (`new Date().toISOString()`). */
  capturedAt: string;
  /**
   * Best-effort identifier of the source page. Set to
   * `context.claim_reference` when it is a non-empty string, otherwise
   * `undefined`. Surfaced in the admin caption to disambiguate captures
   * across different claims.
   */
  claimReference?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Normalise a context bag so it round-trips through `JSON.stringify` /
 * `JSON.parse` losslessly for the values we actually care about
 * (string / number / boolean / null). `undefined` becomes `null` because
 * `JSON.stringify` would silently drop it and the admin still benefits
 * from the "this variable was populated, even if empty" signal. Other
 * non-JSON values (functions, symbols) are also coerced to `null`.
 */
function serialiseContext(
  context: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(context)) {
    const value = context[key];
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      out[key] = value;
      continue;
    }
    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
      try {
        // Round-trip through JSON to drop any non-serialisable nested
        // values; if that fails we fall through to null below.
        out[key] = JSON.parse(JSON.stringify(value));
        continue;
      } catch {
        out[key] = null;
        continue;
      }
    }
    // Covers undefined, function, symbol, bigint.
    out[key] = null;
  }
  return out;
}

function readPersistedStore(): Record<string, RuntimeContextCapture> {
  try {
    const raw = localStorage.getItem(RULE_CONTEXT_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as Record<string, RuntimeContextCapture>;
  } catch {
    return {};
  }
}

function writePersistedStore(store: Record<string, RuntimeContextCapture>): void {
  try {
    localStorage.setItem(RULE_CONTEXT_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Swallow: see readPersistedStore() for rationale.
  }
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let store: Record<string, RuntimeContextCapture> = readPersistedStore();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record the latest context bag seen for a component id.
 *
 * Called from the `withComponentRules` HOC. The bag is normalised via
 * `serialiseContext` before persistence so the round-trip is lossless
 * for the primitives the context schema actually produces.
 */
export function setRuntimeContext(
  id: string,
  context: Record<string, unknown>,
  meta?: { capturedAt?: string; claimReference?: string },
): void {
  const capture: RuntimeContextCapture = {
    context: serialiseContext(context),
    capturedAt: meta?.capturedAt ?? new Date().toISOString(),
  };
  if (meta?.claimReference !== undefined) {
    capture.claimReference = meta.claimReference;
  }
  store = { ...store, [id]: capture };
  writePersistedStore(store);
}

/**
 * Look up the most recently captured context for a component id, or
 * `undefined` when no capture exists yet.
 */
export function getRuntimeContext(id: string): RuntimeContextCapture | undefined {
  return store[id];
}

/**
 * Clear runtime context entries.
 *
 * - `clearRuntimeContext()` — wipes every captured entry.
 * - `clearRuntimeContext("<id>")` — wipes a single slot.
 */
export function clearRuntimeContext(id?: string): void {
  if (id === undefined) {
    store = {};
    writePersistedStore(store);
    return;
  }
  if (id in store) {
    const next = { ...store };
    delete next[id];
    store = next;
    writePersistedStore(store);
  }
}

/**
 * Test-only reset helper. Wipes the in-memory map and the persisted
 * localStorage entry. Do not call from production code.
 */
export function __resetRuntimeContextStoreForTests(): void {
  store = {};
  try {
    localStorage.removeItem(RULE_CONTEXT_STORAGE_KEY);
  } catch {
    // Swallow.
  }
}
